"use client";

import { ConsentCheckbox } from "@/features/quote/ui/ConsentCheckbox";
import { ContactFields } from "@/features/quote/ui/ContactFields";
import { StepHeading } from "@/features/quote/ui/StepHeading";
import { StepNav } from "@/features/quote/ui/StepNav";
import { useStep, useWizard } from "@/features/quote/ui/wizard-context";
import { fieldBaseClass, labelClass } from "@/shared/ui/form/field-styles";
import { Honeypot } from "@/shared/ui/form/Honeypot";
import { SubmitError } from "@/shared/ui/form/SubmitError";

export function ContactStep() {
  const {
    back,
    canAdvance,
    isBusiness,
    fieldErrors,
    setFieldError,
    validateEmailField,
    validatePhoneField,
    honeypot,
    setHoneypot,
    submitError,
    isSubmitting,
    handleSubmit,
  } = useWizard();
  const [
    { contactName = "", companyName = "", email = "", phone = "", notes = "", consent = false },
    answer,
  ] = useStep("contact");

  return (
    <section>
      <StepHeading
        title={
          isBusiness ? "Hvordan skal firmaet kontakte jer?" : "Hvordan skal firmaet kontakte dig?"
        }
        body={
          isBusiness
            ? "I booker ikke noget her. I ser jeres match med det samme, og firmaet ringer jer op med et uforpligtende tilbud. I bestemmer selv, om I vil sige ja. Ingen andre får jeres oplysninger."
            : "Du booker ikke noget her. Du ser dit match med det samme, og firmaet ringer dig op med et uforpligtende tilbud. Du bestemmer selv, om du vil sige ja. Ingen andre får dine oplysninger."
        }
      />

      <Honeypot name="ref_token" value={honeypot} onChange={setHoneypot} />

      {isBusiness ? (
        <div className="mb-5 grid grid-cols-1 gap-3.5 sm:grid-cols-2">
          <div>
            <label htmlFor="wiz-name" className={labelClass}>
              Dit navn
            </label>
            <input
              id="wiz-name"
              type="text"
              autoComplete="name"
              placeholder="Fornavn og efternavn"
              value={contactName}
              onChange={(e) => answer({ contactName: e.target.value })}
              className={fieldBaseClass}
            />
          </div>
          <div>
            <label htmlFor="wiz-company" className={labelClass}>
              Virksomhed
            </label>
            <input
              id="wiz-company"
              type="text"
              autoComplete="organization"
              placeholder="Firmanavn"
              value={companyName}
              onChange={(e) => answer({ companyName: e.target.value })}
              className={fieldBaseClass}
            />
          </div>
        </div>
      ) : (
        <div className="mb-5">
          <label htmlFor="wiz-name" className={labelClass}>
            Dit navn
          </label>
          <input
            id="wiz-name"
            type="text"
            autoComplete="name"
            placeholder="Fornavn og efternavn"
            value={contactName}
            onChange={(e) => answer({ contactName: e.target.value })}
            className={fieldBaseClass}
          />
        </div>
      )}

      <ContactFields
        email={email}
        phone={phone}
        onEmailChange={(v) => {
          answer({ email: v });
          if (fieldErrors.email) setFieldError("email", null);
        }}
        onEmailBlur={(v) => setFieldError("email", validateEmailField(v))}
        onPhoneChange={(v) => {
          answer({ phone: v });
          if (fieldErrors.phone) setFieldError("phone", null);
        }}
        onPhoneBlur={() => setFieldError("phone", validatePhoneField(phone))}
        emailError={fieldErrors.email}
        phoneError={fieldErrors.phone}
      />

      <div className="mb-5">
        <label htmlFor="wiz-notes" className={labelClass}>
          Beskriv opgaven <span className="font-normal text-ink-subtle">(valgfrit)</span>
        </label>
        <textarea
          id="wiz-notes"
          rows={4}
          placeholder={
            isBusiness
              ? "Fx 'Vi har brug for natrengøring mandag til fredag' eller særlige forhold ved lokalerne…"
              : "Fx 'Der er 2 badeværelser og et kontor, der skal særligt gennemgås'…"
          }
          value={notes}
          onChange={(e) => answer({ notes: e.target.value })}
          className={`${fieldBaseClass} min-h-24 resize-y`}
        />
      </div>

      <ConsentCheckbox checked={consent} onChange={(checked) => answer({ consent: checked })} />

      <SubmitError message={submitError} />

      <StepNav
        onBack={back}
        onNext={handleSubmit}
        nextEnabled={canAdvance && !isSubmitting}
        nextLabel={isSubmitting ? "Finder firma…" : "Få et uforpligtende tilbud"}
      />
    </section>
  );
}
